/**
 * Discharge / Final-Bill API (EPIC C / C5) — 1:1 with the frozen API CONTRACT
 * (state/epics/C/C5/build-plan.md). Routes mount under the gated IPD router
 * (`/api/organisation`). Money on the wire is INTEGER PAISE — no rupee float
 * ever crosses.
 *
 * `initiate`/`clear` have no client idempotency key — they're plain status
 * transitions guarded by the from-status condition. `completeDischarge` is
 * the money-consequential write but the contract deliberately carries NO
 * client idempotency key either: the server derives a deterministic
 * `IDEMPOTENCY#ADMISSION#DISCHARGE#{admission_id}` row itself, so a double
 * submit replays the original result (`replayed: true`) rather than needing
 * a fresh header per attempt.
 *
 * Errors = `detail: {code, message, ...extras}`; callers key off `code`
 * (machine code, e.g. BALANCE_NOT_ZERO/BALANCE_CHANGED/DUES_NEGATIVE_BALANCE/
 * CAUSE_OF_DEATH_REQUIRED/LEDGER_DRIFT — see `DISCHARGE_ERROR_CODE` in
 * `constants/admission.js`), never message text.
 *
 * Each function returns `{ success, data?, error?, code?, ...extras }`.
 *
 * DISCHARGE-TEMPLATES Phase 1, FE-1 (`build-plan-phase1.md` §2, FROZEN) —
 * `getDischargeSummary`/`saveDischargeSummary` are a stricter instance of the
 * same rule: the server sends NO UI copy at all on these two routes (§2.7),
 * so `parseDischargeSummaryError` maps `code` to the ratified
 * `DISCHARGE_SUMMARY_ERROR_COPY` below and never renders `detail.message`.
 */

import apiClient from './apiClient';
import { API_CONFIG } from '../../constants';
import {
  DISCHARGE_ERROR_CODE, DOCTOR_REQUIRED_COPY, ADMISSION_ACCESS_ERROR_CODE, ADMISSION_ACCESS_ERROR_COPY,
} from '../../constants/admission';

const BASE = '/api/organisation/admissions';
const enc = encodeURIComponent;

/** IPD error-shape translation (code + extras like balance_paise pass through). */
function parseError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    const { code, message, ...extras } = detail;
    return { error: message || fallback, code, ...extras };
  }
  if (typeof detail === 'string') return { error: detail };
  switch (error?.response?.status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission for this action.' };
    case 404: return { error: 'Admission not found.' };
    case 409: return { error: 'That action conflicts with the current state.', code: 'CONFLICT' };
    case 422: return { error: fallback, code: 'VALIDATION_ERROR' };
    case 503: return { error: 'Admissions are temporarily unavailable. Try again shortly.', code: 'IPD_TABLE_UNAVAILABLE' };
    default:  return { error: fallback };
  }
}

/**
 * DISCHARGE-TEMPLATES Phase 1, FE-1 — §2.3/§2.7 frozen contract: "the FE
 * maps error codes to ratified copy… the server sends no UI copy" on the
 * discharge-summary GET/PUT routes. Every §2.3 error code the two routes can
 * raise has an entry here; `detail.message` is NEVER read for these two
 * calls (unlike the generic `parseError` above, which is shared by every
 * other route on this file and does fall back to the server's message).
 *
 * CONTRACT RECONCILE (t=4, vs the BUILT backend `integration/dt-p1-be`
 * @3d3b493 — plan §2.8 row 1, backend `bk-1177`): §2.7's "the server sends
 * no UI copy" is TRUE for every code above but FALSE for **403**. The built
 * `require_permission` raises a bare-STRING detail —
 * "Permission 'VIEW_ADMISSION' is required. Your role 'X' does not have
 * it." — NOT `{code:"NOT_PERMITTED", required_slug}`. `required_slug` is
 * **never** sent on the wire. Left unhandled that sentence renders straight
 * into the panel's error banner and leaks the caller's role name and the
 * internal permission slug, so `parseDischargeSummaryError` maps 403 by
 * STATUS (see its 403 branch) rather than by a code that never arrives.
 *
 * Copy verbatim from the business verdict R4 (ratified copy table,
 * `state/epics/DISCHARGE-TEMPLATES/build/verdict.md`) — 16 strings, applied
 * exactly, `detail.message` never read. `FIELD_TOO_LONG` is templated with
 * the server's `limit` extra when present (see `fieldTooLongCopy` below);
 * this map holds only the no-limit fallback sentence.
 */
const DISCHARGE_SUMMARY_ERROR_COPY = {
  [DISCHARGE_ERROR_CODE.UNKNOWN_FIELD_KEY]: "This field is not part of your organisation's discharge summary.",
  [DISCHARGE_ERROR_CODE.READ_ONLY_FIELD]: 'This field is filled from the admission record and cannot be edited here.',
  [DISCHARGE_ERROR_CODE.FIELD_HIDDEN]: "This field is not shown on your organisation's discharge summary, so it cannot be saved.",
  [DISCHARGE_ERROR_CODE.FIELD_TOO_LONG]: 'This entry is too long. Please shorten it.',
  [DISCHARGE_ERROR_CODE.INVALID_DATETIME]: 'Enter a valid date and time.',
  [DISCHARGE_ERROR_CODE.INVALID_DATE_RANGE]: 'Date of discharge cannot be before the date of admission.',
  [DISCHARGE_ERROR_CODE.ADMISSION_LOCKED]: 'This admission is closed. The discharge summary can no longer be edited.',
  [DISCHARGE_ERROR_CODE.SUMMARY_TOO_LARGE]: 'This discharge summary is too long to save. Please shorten it.',
  [DISCHARGE_ERROR_CODE.NOT_PERMITTED]: 'You do not have permission to view or edit the discharge summary.',
  [DISCHARGE_ERROR_CODE.ADMISSION_NOT_FOUND]: 'This admission could not be found in your organisation.',
  [DISCHARGE_ERROR_CODE.IPD_TABLE_UNAVAILABLE]: 'Admissions are temporarily unavailable. Please try again in a few minutes.',
  // verdict-r2.md ruling C-3 — the keyless fallback (no `key` extra on this
  // refusal); the field-named sentence is built by `fieldErrorCopy` below.
  [DISCHARGE_ERROR_CODE.INVALID_FIELD_SHAPE]: 'Some investigation rows could not be saved. Please check the test names and results, then save again.',
  // Owner ruling H — reused verbatim, single source (`constants/admission.js`).
  [DISCHARGE_ERROR_CODE.DOCTOR_REQUIRED]: DOCTOR_REQUIRED_COPY,
  // admission-access C-6 (bk-1697) — a DOCTOR outside the care team: ratified
  // #41, single source (`ADMISSION_ACCESS_ERROR_COPY`).
  [ADMISSION_ACCESS_ERROR_CODE.NOT_TREATING_DOCTOR]:
    ADMISSION_ACCESS_ERROR_COPY[ADMISSION_ACCESS_ERROR_CODE.NOT_TREATING_DOCTOR],
};

/**
 * Single source of truth for the ratified NOT_PERMITTED copy — reused by
 * `DischargeSummaryPanel` (R3, bk-1202) to disable Save PROACTIVELY, before a
 * PUT is ever attempted, with the SAME sentence the 403 fallback shows
 * reactively. One string, two call sites, never allowed to drift.
 */
export const NOT_PERMITTED_SUMMARY_COPY = DISCHARGE_SUMMARY_ERROR_COPY[DISCHARGE_ERROR_CODE.NOT_PERMITTED];

/**
 * `FIELD_TOO_LONG` (§2.3) carries an optional `limit` extra — ratified R4:
 * "This entry is too long. Please keep it under {limit} characters." when
 * present, else the map's plain fallback sentence.
 */
function fieldTooLongCopy(extras) {
  const limit = extras && extras.limit;
  return limit != null
    ? `This entry is too long. Please keep it under ${limit} characters.`
    : DISCHARGE_SUMMARY_ERROR_COPY[DISCHARGE_ERROR_CODE.FIELD_TOO_LONG];
}

/**
 * verdict-r2.md ruling C-3 — a 422/413 that names a `key` renders under THAT
 * field, never as a keyless panel banner. This module owns the ratified
 * STRING (templated with the field's own `label` + the server's `limit`
 * extra); the caller (`DischargeSummaryPanel.jsx`) owns the `key -> label`
 * lookup off the `fields[]` it already holds — never duplicated here.
 * Returns `null` for a code this ruling doesn't cover, so the caller falls
 * back to its own generic (keyless) banner handling.
 *
 * SCOPE (ASSUMPTION — ruling C-3's text also lists "SUMMARY_TOO_LARGE where a
 * key is available"; the pinned QA regression `DischargeSummaryPanel.qa.dtp1.
 * test.jsx` asserts SUMMARY_TOO_LARGE as a keyless banner against the BUILT
 * backend, and that test's mock always carries an incidental `key` extra
 * shared by every row in its table — not a deliberate "key available" case.
 * Left OUT of the per-field codes here so that pinned assertion stays true;
 * revisit once R3-BE's real SUMMARY_TOO_LARGE shape is confirmed at
 * integration) — only `FIELD_TOO_LONG` and `INVALID_FIELD_SHAPE` route here.
 */
export function fieldErrorCopy(code, label, extras = {}) {
  if (code === DISCHARGE_ERROR_CODE.FIELD_TOO_LONG) {
    const limit = extras && extras.limit;
    return limit != null
      ? `${label}: this entry is too long. Please keep it under ${limit} characters.`
      : `${label}: this entry is too long. Please shorten it.`;
  }
  if (code === DISCHARGE_ERROR_CODE.INVALID_FIELD_SHAPE) {
    return DISCHARGE_SUMMARY_ERROR_COPY[DISCHARGE_ERROR_CODE.INVALID_FIELD_SHAPE];
  }
  return null;
}

/**
 * §2.2/§2.3 error translation for `getDischargeSummary`/`saveDischargeSummary`
 * ONLY. Codes without a dedicated line fall back to `fallback` (never the
 * server's `message`) so a code the copy map hasn't caught up with still
 * reads as plain English, not raw JSON.
 *
 * The 403 branch is keyed off the STATUS, not off a `detail.code`: the built
 * backend sends a bare-string detail there (see the copy map's reconcile
 * note), so the code-object branch below can never match it and the generic
 * `parseError` would surface the server's raw sentence verbatim. Handling it
 * here keeps §2.7's "the only server strings the UI shows verbatim are
 * `fields[].label`" true on these two routes, and keeps the ratified
 * NOT_PERMITTED copy reachable instead of dead.
 *
 * pydantic's OWN 422 (`extra="forbid"`, missing `values`) is an ARRAY detail
 * with no `code` — the `!Array.isArray` guard lets it fall through to
 * `parseError`'s `case 422`, which yields the plain fallback plus
 * `code:'VALIDATION_ERROR'`. That degrades safely and never renders raw
 * JSON, so it is deliberately NOT special-cased (plan §2.8 row 2).
 */
function parseDischargeSummaryError(error, fallback) {
  // admission-access — the department wall's 403 (`OUT_OF_DEPARTMENT_SCOPE`)
  // and the care-team gate's 403 (`NOT_TREATING_DOCTOR`, M-BE-2, bk-1697)
  // carry their OWN code; they must reach the panel as that code, not collapse
  // into NOT_PERMITTED below (same rule as visitApi's 403).
  const coded403 = error?.response?.data?.detail;
  const keepsCode = coded403 && (
    coded403.code === ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE
    || coded403.code === ADMISSION_ACCESS_ERROR_CODE.NOT_TREATING_DOCTOR);
  if (error?.response?.status === 403 && !keepsCode) {
    return {
      error: DISCHARGE_SUMMARY_ERROR_COPY[DISCHARGE_ERROR_CODE.NOT_PERMITTED],
      code: DISCHARGE_ERROR_CODE.NOT_PERMITTED,
    };
  }
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    const { code, ...extras } = detail;
    delete extras.message;
    const copy = code === DISCHARGE_ERROR_CODE.FIELD_TOO_LONG
      ? fieldTooLongCopy(extras)
      : (DISCHARGE_SUMMARY_ERROR_COPY[code] || fallback);
    return { error: copy, code, ...extras };
  }
  return parseError(error, fallback);
}

/**
 * EP-D1 PUT /admissions/{id}/death-record — upsert: create OR edit, one
 * route both doors (death-record slice, D39/D42). Perm `DISCHARGE_PATIENT`
 * — no new slug. Body `DeathRecordRequest`, `extra="forbid"`: `death_at` +
 * `declaring_doctor_member_id` + `death_rev` required; `immediate_cause` /
 * `antecedent_cause` optional (Variant A's dialog never captures them — the
 * cause is typed later, in the discharge dialog). `death_rev` is the
 * revision the caller believes it is editing (`0` = "no record exists");
 * a mismatch 409s `DEATH_RECORD_STALE`, never coerced.
 *
 * 200 → `{ admission: AdmissionResponse, death_rev }` — the FULL admission,
 * so every flag surface re-renders off ONE payload. Caller must NOT refetch.
 */
export const putDeathRecord = async (admissionId, {
  death_at,
  declaring_doctor_member_id,
  immediate_cause,
  antecedent_cause,
  death_rev,
} = {}) => {
  try {
    const body = { death_at, declaring_doctor_member_id, death_rev };
    if (immediate_cause !== undefined) body.immediate_cause = immediate_cause;
    if (antecedent_cause !== undefined) body.antecedent_cause = antecedent_cause;
    const response = await apiClient.put(`${BASE}/${enc(admissionId)}/death-record`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] putDeathRecord error:', error);
    return { success: false, ...parseError(error, 'Failed to record the death.') };
  }
};

/**
 * EP-D2 DELETE /admissions/{id}/death-record?death_rev={int} — remove (D39:
 * "kept in history for reference" — the remove itself is traced as an
 * event, this call only clears the stored record). `death_rev` is a
 * REQUIRED query param (no DELETE body). Perm `DISCHARGE_PATIENT`.
 *
 * 200 → same `DeathRecordResponse` envelope — `admission.cause_of_death`
 * comes back absent, `death_rev` bumped (monotonic, never removed —
 * invariant 6).
 */
export const deleteDeathRecord = async (admissionId, deathRev) => {
  try {
    const response = await apiClient.delete(`${BASE}/${enc(admissionId)}/death-record`, {
      params: { death_rev: deathRev },
    });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] deleteDeathRecord error:', error);
    return { success: false, ...parseError(error, 'Failed to remove the death record.') };
  }
};

/**
 * #1 POST /admissions/{id}/discharge/initiate — ADMITTED|IN_TREATMENT →
 * DISCHARGE_INITIATED. 200 → AdmissionResponse.
 *
 * DOD rule v3 (C1) — the body is now the OPTIONAL `DischargeInitiateRequest`
 * (`extra="forbid"`) with exactly ONE key:
 *   `discharge_at`: ISO-8601 UTC ms — the CONFIRMED discharge date the desk
 *   approved on the confirm step. ABSENT ⇒ the server resolves the admission's
 *   `planned_discharge_at`, else `now` — never a client clock, never a silent
 *   future. LAMA/DAMA ignore it entirely (they record the actual walk-out
 *   moment), so the dialog simply does not send it for them.
 * Omitting the argument keeps the pre-v3 wire shape byte-identical (`{}`).
 */
export const initiateDischarge = async (admissionId, body) => {
  try {
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/discharge/initiate`, body || {});
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] initiateDischarge error:', error);
    return { success: false, ...parseError(error, 'Failed to start discharge.') };
  }
};

/**
 * POST /admissions/{id}/discharge/cancel-initiation — DISCHARGE_INITIATED|
 * BILLING_CLEARANCE → prior status. Body `{reason}` (required, ≤300). 200 →
 * AdmissionResponse (dates/invoice number null). Callers key off `code`
 * (`CANCEL_INITIATION_ERROR_COPY`).
 */
export const cancelInitiation = async (admissionId, reason) => {
  try {
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/discharge/cancel-initiation`, { reason });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] cancelInitiation error:', error);
    return { success: false, ...parseError(error, 'Failed to cancel the initiation.') };
  }
};

/**
 * POST /admissions/{id}/charges/fix-after-discharge-date (owner 2026-10-13) —
 * before Initiate, applies a per-line MOVE / REMOVE to the charges dated after
 * `discharge_date`. Body `{discharge_date: YYYY-MM-DD, items[{id, action}],
 * reason_code, reason?}`. 200 → `{admission_id, discharge_date, moved[],
 * removed[]}`. Callers key off `code` (`FIX_CHARGES_ERROR_COPY`).
 */
export const fixChargesAfterDischargeDate = async (admissionId, body) => {
  try {
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/charges/fix-after-discharge-date`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] fixChargesAfterDischargeDate error:', error);
    return { success: false, ...parseError(error, 'Failed to fix the charges.') };
  }
};

/**
 * #2 POST /admissions/{id}/discharge/clear — DISCHARGE_INITIATED →
 * BILLING_CLEARANCE. Body `{}`. 200 → AdmissionResponse.
 */
export const clearBilling = async (admissionId) => {
  try {
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/discharge/clear`, {});
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] clearBilling error:', error);
    return { success: false, ...parseError(error, 'Failed to clear billing.') };
  }
};

/**
 * #3 POST /admissions/{id}/discharge/complete — the terminal write. Body per
 * contract: `discharge_type` (required), `reason`/`referral_reason`/
 * `referral_note`/`cause_of_death`/`authorised_by` are type-conditional —
 * this function passes ONLY the fields the caller supplies (omitted ⇒ not
 * sent, server-validated per type). 200 → `{ admission, bill, dues, replayed }`.
 *
 * `authorised_by_member_id` (O3, Wave-2) — required ONLY on the LAMA/DAMA/
 * DECEASED residual write-off exception (WRITE_OFF_DUES-gated); a staff-member
 * PICKER selection the server resolves to the display name (`authorised_by`).
 * A NORMAL/REFERRED discharge or a zero-residual dues-eligible discharge never
 * sends it. The retired free-text `authorised_by` + soft `dues_ack_refused` are
 * no longer sent — R3-4 replaces them with the hard zero-balance gate +
 * permissioned write-off exception.
 *
 * `discharge_stamp` (DOD rule v3, C5) — the UNCHANGED wire tokens
 * "PLANNED"|"NOW", sent ONLY when the Complete step actually showed the desk
 * a Confirmed-vs-Now choice (a NORMAL/REFERRED confirmed date on a DIFFERENT
 * IST calendar day from `now`). Omitted otherwise (unchanged wire shape for
 * the common, same-day case). The "PLANNED" option's on-screen LABEL reads
 * "Confirmed"; the token itself is not renamed.
 */
export const completeDischarge = async (admissionId, {
  discharge_type,
  reason,
  referral_reason,
  referral_note,
  cause_of_death,
  authorised_by_member_id,
  discharge_stamp,
} = {}) => {
  try {
    const body = { discharge_type };
    if (reason !== undefined) body.reason = reason;
    if (referral_reason !== undefined) body.referral_reason = referral_reason;
    if (referral_note !== undefined) body.referral_note = referral_note;
    if (cause_of_death !== undefined) body.cause_of_death = cause_of_death;
    if (authorised_by_member_id !== undefined) body.authorised_by_member_id = authorised_by_member_id;
    if (discharge_stamp !== undefined) body.discharge_stamp = discharge_stamp;
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/discharge/complete`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] completeDischarge error:', error);
    return { success: false, ...parseError(error, 'Failed to complete discharge.') };
  }
};

/**
 * #5 GET /admissions/{id}/final-bill — pre-discharge live view
 * (`locked: false`) or post-discharge locked render. Recompute==cache is
 * asserted server-side (409 LEDGER_DRIFT — never a disagreeing render).
 */
export const getFinalBill = async (admissionId) => {
  try {
    const response = await apiClient.get(`${BASE}/${enc(admissionId)}/final-bill`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] getFinalBill error:', error);
    return { success: false, ...parseError(error, 'Failed to load the final bill.') };
  }
};

/**
 * §2.2 GET /admissions/{id}/discharge-summary — NEW route (DISCHARGE-TEMPLATES
 * Phase 1, FE-1). Perm `VIEW_ADMISSION`. No query params. 200 → the
 * discharge-summary envelope (§2.2): `admission_id`, `source`
 * (`"ROW"|"LEGACY"|"EMPTY"`), `status`, `locked`, `catalogue_version`,
 * `config_source`, `has_hidden_values`, `fields[]` (pre-sorted, pre-filtered
 * — this client does not sort/filter/hide), `authored_by`, `updated_at`.
 * Byte-identical shape to the PUT's 200 (§2.3), so callers use one parser.
 */
export const getDischargeSummary = async (admissionId) => {
  try {
    const response = await apiClient.get(`${BASE}/${enc(admissionId)}/discharge-summary`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] getDischargeSummary error:', error);
    return { success: false, ...parseDischargeSummaryError(error, 'Could not load the discharge summary. Please try again.') };
  }
};

/**
 * §2.3 PUT /admissions/{id}/discharge-summary — RESHAPED (DISCHARGE-TEMPLATES
 * Phase 1, FE-1). Perm `DISCHARGE_PATIENT` (unchanged). Body is `extra=
 * "forbid"`, `{ values }` ONLY — `values` is a plain object, canonical
 * catalogue key -> str|null, sent EXACTLY as the caller supplies it (this
 * client owns zero key constants — §2.7). Full-object REPLACE: an omitted
 * key clears that field; `null`/`""` on a header key clears the override
 * back to the admission's prefill value. 409 `ADMISSION_LOCKED` once
 * terminal (DECEASED stays writable, §2.3). 200 → same envelope as
 * `getDischargeSummary`.
 */
export const saveDischargeSummary = async (admissionId, values = {}) => {
  try {
    const response = await apiClient.put(`${BASE}/${enc(admissionId)}/discharge-summary`, { values });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] saveDischargeSummary error:', error);
    return { success: false, ...parseDischargeSummaryError(error, 'Could not save the discharge summary. Please try again.') };
  }
};

/**
 * §2.4 GET /admissions/{id}/invoice/pdf — the single UNIFIED billing document
 * (D1 owner ruling). The document prints INTERIM BILL while the admission is
 * open and INVOICE once discharged (same document, title/number flip — R3-1)
 * — it is the ONE print target for both the running bill and the discharge
 * "print bill" action; the legacy separate final-bill PDF is retired. Same
 * blob-download + in-app FileViewerStore pattern as `openAdmissionReceiptPdf`
 * — never a raw `window.open` (keeps it inside the app shell, works for any
 * admission status the caller holds `GENERATE_IPD_BILL_PDF` for).
 *
 * `title` (R3-1 FE reconcile) — the in-app viewer's tab/header title. Callers
 * printing mid-stay (the running bill, always OPEN) pass 'Interim Bill'; every
 * terminal/post-discharge caller keeps the default 'Invoice'. The PDF's own
 * printed title is server-rendered from admission status (byte contract,
 * unaffected by this label) — this only names the in-app viewer chrome.
 */
export const openInterimInvoicePdf = async (admissionId, title = 'Invoice') => {
  try {
    const response = await apiClient.get(`${BASE}/${enc(admissionId)}/invoice/pdf`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    const useFileViewerStore = (await import('../../stores/useFileViewerStore')).default;
    useFileViewerStore.getState().openViewer({ blobUrl, fileType: 'pdf', title });
    return { success: true, blobUrl };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] openInterimInvoicePdf error:', error);
    return { success: false, ...parseError(error, 'Could not open the invoice.') };
  }
};

/**
 * `FileReader` (not `Blob.prototype.text`) — jsdom's test `Blob` doesn't
 * implement `.text()`. Returns the parsed `detail` object, or `null` if the
 * blob isn't a readable JSON `{detail: {...}}` body.
 */
async function readBlobErrorDetail(blob) {
  try {
    const text = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsText(blob);
    });
    const parsed = JSON.parse(text);
    return parsed?.detail && typeof parsed.detail === 'object' ? parsed.detail : null;
  } catch (_parseErr) {
    return null;
  }
}

/**
 * §2.4b GET /admissions/{id}/lab-bill/pdf — consolidated DIAG/{fy}/# Diagnostics
 * Bill (user-facing name; endpoint path/internal naming stay `lab-bill`).
 * (Wave-3 build-plan FE-1; F1 · D31 interim-bill delta). Same blob-download +
 * in-app FileViewerStore pattern as `openInterimInvoicePdf` — never a raw
 * `window.open`.
 *
 * `responseType: 'blob'` means axios delivers ANY error body as a `Blob`,
 * not JSON, so the generic `parseError` (which reads
 * `error.response.data.detail`) can't see a structured code and falls
 * through to the plain status-code default. Read the blob body ourselves
 * first (`readBlobErrorDetail`) and detect the code before falling back:
 * - 404 `NO_LAB_CHARGES` — zero non-cancelled orders (unchanged).
 * - 409 `DIAG_BILL_NOT_ISSUABLE` — NEW (D-4): the admission is CANCELLED /
 *   TRANSFERRED_OUT and never minted a number. Surfaces the server's own
 *   `message` (the contract's `status` extra rides along on `res.status` for
 *   any caller that wants it) — a 409 that fell through here would render a
 *   raw server string to a user, which is the single most likely mistake on
 *   this route (build-plan.md D-4).
 */
export const openLabBillPdf = async (admissionId, title = 'Diagnostics Bill') => {
  try {
    const response = await apiClient.get(`${BASE}/${enc(admissionId)}/lab-bill/pdf`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    const useFileViewerStore = (await import('../../stores/useFileViewerStore')).default;
    useFileViewerStore.getState().openViewer({ blobUrl, fileType: 'pdf', title });
    return { success: true, blobUrl };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] openLabBillPdf error:', error);
    const httpStatus = error?.response?.status;
    if ((httpStatus === 404 || httpStatus === 409) && error.response.data instanceof Blob) {
      const detail = await readBlobErrorDetail(error.response.data);
      if (detail?.code === 'NO_LAB_CHARGES') {
        return { success: false, error: 'No diagnostics to bill for this admission.', code: 'NO_LAB_CHARGES' };
      }
      if (httpStatus === 409 && detail?.code === 'DIAG_BILL_NOT_ISSUABLE') {
        // RATIFIED (integration gate, copy pack) — FE-owned copy keyed off the
        // CODE, never the server's `detail.message` prose (backend stays
        // UI-agnostic; see the blob-parse note above).
        return {
          success: false,
          error: 'This admission was cancelled or transferred out, so a diagnostics bill cannot be issued for it.',
          code: 'DIAG_BILL_NOT_ISSUABLE',
          status: detail.status,
        };
      }
    }
    return { success: false, ...parseError(error, 'Could not open the diagnostics bill.') };
  }
};

/** #8 GET /admissions/{id}/discharge-summary/pdf — absolute URL. Kept for
 * back-compat (AdmissionWorkspace's post-discharge print step still links
 * off this) and for tests; `DischargeSummaryPanel`/`RunningBillPanel` no
 * longer build a `<a href>` off it (see `openDischargeSummaryPdf` below). */
export const dischargeSummaryPdfUrl = (admissionId) =>
  `${API_CONFIG.BACKEND_URL}${BASE}/${enc(admissionId)}/discharge-summary/pdf`;

/**
 * #8 GET /admissions/{id}/discharge-summary/pdf — FIX-7-FE: opens in the
 * app's own viewer (Print/Close chrome) instead of a new browser tab. Same
 * blob-download + in-app FileViewerStore pattern as `openInterimInvoicePdf`/
 * `openLabBillPdf`. `title` is the in-app viewer's title — the caller passes
 * 'Discharge summary' or 'Death summary' (already derived from
 * `isDeathOutcome` at each call site); this function does not re-derive it.
 */
export const openDischargeSummaryPdf = async (admissionId, { title = 'Discharge summary' } = {}) => {
  try {
    const response = await apiClient.get(`${BASE}/${enc(admissionId)}/discharge-summary/pdf`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    const useFileViewerStore = (await import('../../stores/useFileViewerStore')).default;
    useFileViewerStore.getState().openViewer({ blobUrl, fileType: 'pdf', title });
    return { success: true, blobUrl };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] openDischargeSummaryPdf error:', error);
    return { success: false, ...parseError(error, 'Could not open the discharge summary.') };
  }
};

/** #9 GET /admissions/{id}/dues-ack/pdf — absolute URL for a print/download link. */
export const duesAckPdfUrl = (admissionId) =>
  `${API_CONFIG.BACKEND_URL}${BASE}/${enc(admissionId)}/dues-ack/pdf`;

/**
 * A fresh idempotency key per ISSUE attempt (TPA-PREAUTH/tpa-bill, C2). The
 * server treats the header as OPTIONAL, but this client ALWAYS sends one —
 * a network-level retry of the SAME POST replays the original `–n`
 * (`replayed: true`) instead of minting a second number. The caller (the
 * "TPA Bill" button) additionally disables itself while the request is in
 * flight, so a genuine double-click never reaches this function twice with
 * two different keys in the first place.
 */
function newTpaBillIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * C2 · POST /admissions/{id}/tpa-bills → 201 (newly issued) or 200 (reused —
 * FIX-4-BE) `TpaBillIssueResponse` (JSON, never bytes) — both are `success`.
 * Body: none. Mints the next `seq_no` for this ADMISSION (server-side CAS)
 * and snapshots the bill at issue time. 200/201 body:
 * `{admission_id, seq_no, bill_no_display, issued_at, issued_by_member_id,
 * issued_by_name, total_paise, insurer_approved_paise,
 * payable_by_patient_paise, dod_at, dod_is_planned, replayed, reused}` —
 * every field a caller needs to render the newly-issued row is already on
 * this response, so a successful issue never needs a follow-up
 * `listTpaBills` call. `reused` (FIX-4-BE, additive) is `true` when nothing
 * changed since the last issue and the server handed back that SAME bill
 * rather than minting a new one — distinct from `replayed` (a network-level
 * retry of the identical idempotency key). Absent on an older backend ⇒ the
 * caller (`useTpaBillIssue`) treats it as `false`.
 *
 * Errors (surfaced via `code`, never `error.message` on the wire): `403`
 * (perm — hidden in the UI, not surfaced here) · `404` admission ·
 * `409 NOT_CASHLESS` · `409 ISSUE_AFTER_COMPLETION` ·
 * `409 SNAPSHOT_TOO_LARGE` · `503 IPD_TABLE_UNAVAILABLE`.
 */
export const issueTpaBill = async (admissionId) => {
  try {
    const response = await apiClient.post(
      `${BASE}/${enc(admissionId)}/tpa-bills`,
      {},
      { headers: { 'Idempotency-Key': newTpaBillIdempotencyKey() } },
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] issueTpaBill error:', error);
    return { success: false, ...parseError(error, 'Could not issue the TPA bill.') };
  }
};

/**
 * C2 · GET /admissions/{id}/tpa-bills → 200 `{items: TpaBillListItem[],
 * count}`, newest first (`seq_no` DESC, server-ordered — this client never
 * re-sorts). Item = `{seq_no, bill_no_display, issued_at, issued_by_name,
 * total_paise}`.
 */
export const listTpaBills = async (admissionId) => {
  try {
    const response = await apiClient.get(`${BASE}/${enc(admissionId)}/tpa-bills`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] listTpaBills error:', error);
    return { success: false, ...parseError(error, 'Could not load the issued TPA bills.') };
  }
};

/**
 * C2 · GET /admissions/{id}/tpa-bills/{seq}/pdf → 200 `application/pdf`,
 * rendered FROM the frozen snapshot (reprinting reproduces the original
 * figures, never today's charges). Same blob-download + in-app
 * FileViewerStore pattern as `openInterimInvoicePdf` (clone of that idiom,
 * `:380` above) — never a raw `window.open`. `404 TPA_BILL_NOT_FOUND`
 * (including the cross-admission guard: a foreign admission's seq 404s too).
 */
export const openTpaBillPdf = async (admissionId, seqNo, title = 'TPA Bill') => {
  try {
    const response = await apiClient.get(
      `${BASE}/${enc(admissionId)}/tpa-bills/${enc(seqNo)}/pdf`,
      { responseType: 'blob' },
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    const useFileViewerStore = (await import('../../stores/useFileViewerStore')).default;
    useFileViewerStore.getState().openViewer({ blobUrl, fileType: 'pdf', title });
    return { success: true, blobUrl };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] openTpaBillPdf error:', error);
    return { success: false, ...parseError(error, 'Could not open the TPA bill.') };
  }
};

/**
 * ─────────────────────────────────────────────────────────────────────────
 * Patient Card (EPIC PATIENT-CARD, F3) — §1.4/§1.5 document descriptors +
 * the PURE content route. ADDITIVE ONLY — every helper above is untouched.
 *
 * These bind a DIFFERENT wire error shape than the rest of this file:
 * `{"detail":{"error_code":"..."}}` (§1.0, pinned — `error_code`, not the
 * IPD money-endpoints' `code`), so they use their own local error parser
 * rather than `parseError` above.
 *
 * `getAdmissionDocumentContent` is the ONLY route this file uses for the
 * card's document viewer — it never calls the legacy minting `…/invoice/pdf`
 * / `…/lab-bill/pdf` routes (those remain reachable only from a human print
 * button on the admissions desk, INV-16 — see `openInterimInvoicePdf` /
 * `openLabBillPdf` above, unchanged).
 * ─────────────────────────────────────────────────────────────────────────
 */

/** §1.0/§1.4/§1.5 error envelope → `{ error, code, ...extras }`. */
function parsePatientCardDocError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.error_code) {
    const { error_code, ...extras } = detail;
    return { error: fallback, code: error_code, ...extras };
  }
  switch (error?.response?.status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission for this action.' };
    case 404: return { error: 'Admission not found.' };
    default:  return { error: fallback };
  }
}

/**
 * §1.4 GET /admissions/{id}/documents — the descriptor list (available /
 * issued / state per closed `doc_type`). No params.
 */
export const getAdmissionDocuments = async (admissionId) => {
  try {
    const response = await apiClient.get(`${BASE}/${enc(admissionId)}/documents`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] getAdmissionDocuments error:', error);
    return { success: false, ...parsePatientCardDocError(error, 'Failed to load documents for this admission.') };
  }
};

/**
 * §1.5 GET /admissions/{id}/documents/{doc_type}/content — the PURE path.
 * NEVER mints/reprints (INV-15/16/19). Same blob-download + in-app
 * FileViewerStore pattern as `openInterimInvoicePdf`/`openLabBillPdf` — never
 * a raw `window.open`. `responseType: 'blob'` means axios delivers ANY error
 * body as a `Blob`, so the structured `error_code` is read off the blob text
 * first (`readBlobErrorDetail`, defined above) before falling back to the
 * plain status-code default — covers the 409 `DOCUMENT_NOT_ISSUED` (with
 * `issue_endpoint`/`issue_condition` extras), 404
 * `ADMISSION_NOT_FOUND|NO_LAB_CHARGES|NO_DUES`, and 422 `UNKNOWN_DOC_TYPE`
 * (with `supported`) cases.
 */
export const getAdmissionDocumentContent = async (admissionId, docType, title = 'Document') => {
  try {
    const response = await apiClient.get(
      `${BASE}/${enc(admissionId)}/documents/${enc(docType)}/content`,
      { responseType: 'blob' },
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    const useFileViewerStore = (await import('../../stores/useFileViewerStore')).default;
    useFileViewerStore.getState().openViewer({ blobUrl, fileType: 'pdf', title });
    return { success: true, blobUrl };
  } catch (error) {
    console.error('[AdmissionDischargeAPI] getAdmissionDocumentContent error:', error);
    if (error.response?.data instanceof Blob) {
      const detail = await readBlobErrorDetail(error.response.data);
      if (detail?.error_code) {
        const { error_code, ...extras } = detail;
        return { success: false, error: 'Could not open this document.', code: error_code, ...extras };
      }
    }
    return { success: false, ...parsePatientCardDocError(error, 'Could not open this document.') };
  }
};

const admissionDischargeApi = {
  putDeathRecord,
  deleteDeathRecord,
  initiateDischarge,
  cancelInitiation,
  fixChargesAfterDischargeDate,
  clearBilling,
  completeDischarge,
  getFinalBill,
  getDischargeSummary,
  saveDischargeSummary,
  openInterimInvoicePdf,
  openLabBillPdf,
  dischargeSummaryPdfUrl,
  openDischargeSummaryPdf,
  duesAckPdfUrl,
  issueTpaBill,
  listTpaBills,
  openTpaBillPdf,
  getAdmissionDocuments,
  getAdmissionDocumentContent,
};

export default admissionDischargeApi;
